import Sample from 'repos/Sample.js';
import ethereum from "decentralized/ethereum.js"
import {shuffle} from 'utils/ShuffleArray.js';
import {getSamplesStream} from 'search/getSamplesStream.js';
import EthereumSoundsSearcher from 'search/EthereumSoundsSearcher.js';
import {uuid} from 'uuidv4';
const BUFFER_SIZE = 1;
const TIMEOUT=7000;
const MAX_ALLOWED_ATTEMPTS= 12;

const TAGS_TO_SEARCH = [
    'hi-hat', 'snare', 'kick', 'crash',
    'ride', 'breaks', 'sherman', 'ambient',
    'sub', 'rim', 'side-stick', 'sidestick', 'clap',
    "Synth",
    "perc",
    'piano', 'guitar', 'rimshot', 'percussion'
];

class SamplesStream {
    constructor(sample, sequencerEngine) {
        this.id = uuid();
        this.sample = sample;
        this.sequencerEngine = sequencerEngine;
        this.searcher = new EthereumSoundsSearcher(
            sequencerEngine.ethereumCache, sequencerEngine);
        this.nextSounds = [];
        this.previousSounds = [];
        this.counter = 0;
        this._tagSounds = undefined;
    }

    get length() {
        return this._sounds && this._sounds.length;
    }

    set sounds(s) {
        this._sounds = s;
    }

    set tagSounds(s) {
        this.nextSounds = [];
        this.previousSounds = [];
        this._tagSounds = s;
    }

    get sounds() {
        return this._tagSounds || this._sounds;
    }

    getCopy(sample=this.sample) {
        let copy = new SamplesStream(sample, this.sequencerEngine);
        copy.searcher = this.searcher;
        if (this.sounds) {
            copy.sounds = [... this.sounds];
        }
        copy.tags = this.tags;
        copy.initBuffers();
        return copy;
    }

    async fetch() {
        if (!this.sample || !this.sample.tags) {
            return;
        }
        let tags = this.sample.tags;
        let guildIds = await ethereum.getGuildsWithAccess();
        let results = await getSamplesStream(tags, guildIds);
        this.sounds = results;
        this.tags = tags;
    }

    hasNext() {
        return this.sounds === undefined || this.nextSounds.length > 0;
    }

    hasPrevious() {
        return this.sounds === undefined || this.previousSounds.length > 0;
    }

    dispose() {
        if (this.nextSounds) {
            this.nextSounds.forEach(s => s.dispose(true));
        }
        if (this.previousSounds) {
            this.previousSounds.forEach(s => s.dispose(true));
        }
        this.sounds = undefined;
        this.nextSounds = undefined;
        this.previousSounds = undefined;
        if (this.sample) {
            this.sample.dispose(true);
            this.sample = undefined;
        }

        this.searcher = undefined;
    }

    async search() {
        let tags = this.sample.tags;
        this.tags = tags;
        this.searcher.selectedTags = tags;
        this.sounds =  (await getSamplesStream(tags)).slice(1);
        this.counter = 0;
        this.initBuffers();
    }

    async initBuffers() {
        if (this.sounds === undefined || this.nextSounds === undefined) {
            return;
        }
        this.loading = true;
        let len = this.sounds.length;
        let i=0;
        while (this.nextSounds.length < BUFFER_SIZE) { 
            try {
                let {next, previous} = await this.loadNextForInitialBuffer(i);
                this.nextSounds.push(next);
                this.previousSounds.push(previous);
            } catch (err) {
                if (i >= MAX_ALLOWED_ATTEMPTS) {
                    break;
                }
            }
            i++;
            if (this.nextSounds === undefined) {
                break;
            }
        }
        this.loading = false;
    }

    loadNextForInitialBuffer(i) {
        return new Promise(async (resolve, reject) => {
            setTimeout(() => {
                reject();
            }, TIMEOUT);
            try {
                let len = this.sounds.length;
                let id = (this.counter + i) % len;
                let sampleId = this.sounds[id];
                let sample = new Sample(sampleId);
                await sample.load()
                // await sample.analyze(this.sequencerEngine.aubio);

                id = (((this.counter - i - 1) % len )+len)%len;
                sampleId = this.sounds[id];
                let sample2 = new Sample(sampleId);
                await sample2.load()
                    .catch(reject);
                resolve({
                    next: sample,
                    previous: sample2
                });
            } catch (err) {
                reject();
            }
        });
    }

    sleep(x) {
        return new Promise(async (resolve, reject) => {
            setTimeout(() => resolve(), x);
        });
    }

    async getNext() {
        if (this.sounds === undefined) {
            await this.search();
        }
        while (this.loading) {
            await this.sleep(50);
        }
        let nextSound = this.nextSounds.shift();
        setTimeout(() => {
            this.loadNext(nextSound);
        }, 100);
        return nextSound;
    }

    async getPrevious() {
        if (this.sounds === undefined) {
            await this.search();
        }
        while (this.loading) {
            await this.sleep(50);
        }
        let previousSound = this.previousSounds.shift();
        this.loadPrevious(previousSound);
        return previousSound;
    }

    async seek(c) {
        this.counter = c;
        let sample = await this.loadNextAvailableSample();
        this.nextSounds = [sample];
        return await this.getNext();
    }

    async loadNext(nextSound) {
        let loaded = false;
        setTimeout(() => {
            if (!loaded) {
                this.loading = false;
            }
        }, TIMEOUT);

        this.loading = true;

        // unload the previous sound 
        let oldSample = this.previousSounds.pop();
        if (oldSample) {
            oldSample.dispose(true);
        }
        this.previousSounds = [this.sample, ... this.previousSounds];
        this.sample = nextSound;

        // load the next
        let running = true;
        let sample = undefined;
        let tries = 0;
        while (sample === undefined) {
            try {
                sample = await this.loadNextAvailableSample();
            } catch (err) {
                if (tries >= MAX_ALLOWED_ATTEMPTS) {
                    this.loading = false;
                    loaded = true;
                    return;
                }
            }
            tries++;
        }

        this.nextSounds.push(sample);
        this.loading = false;
        loaded = true;
    }

    loadNextAvailableSample() {
        return new Promise(async (resolve, reject) => {
            this.counter = (this.counter + 1) % this.sounds.length;
            let id = (this.counter + BUFFER_SIZE) % this.sounds.length;
            let sampleId = this.sounds[id];
            let sample = new Sample(sampleId);
            
            setTimeout(() => {
                reject();
            }, TIMEOUT);

            try {
                await sample.load();
                resolve(sample);
            } catch (err) {
                reject();
            }
        });
    }

    async loadPrevious(previousSound) {
        this.loading = true;
        let oldSample = this.nextSounds.pop();
        if (oldSample) {
            oldSample.dispose(true);
        }
        this.nextSounds = [this.sample, ... this.nextSounds];
        this.sample = previousSound;
        this.counter = this.mod(this.counter - 1, this.sounds.length);
        let id = this.mod(this.counter - BUFFER_SIZE, this.sounds.length);
        let sampleId = this.sounds[id];
        let sample = new Sample(sampleId);
        await sample.load();
        // await sample.analyze(this.sequencerEngine.aubio);
        this.previousSounds.push(sample);
        this.loading = false;
    }

    mod(a, b) {
        return ((a % b )+ b ) % b ;
    }
}

export default SamplesStream;
